import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useRazorpayVbankPaymentsQuery, useRazorpayVbankQuery } from 'v2/pages/billing/queries'
import { getAccountId } from 'v2/pages/billing/utils'
import { billingQueryKeys } from 'v2/shared/utils/query-key-management'
import { errorToast, successToast } from 'v2/utils/toasts'
import { BankTransferDrawerView } from './bank-transfer-drawer-view'
import {
  baselinePaymentIds,
  findNewCapturedPayment,
  findNewFailedPayment,
} from './razorpay-vbank-payments'

interface RazorpayBankTransferDrawerProps {
  open: boolean
  onClose: () => void
  // The active vbank id, resolved by useEnsureActiveRazorpayVbank
  // before the drawer opens. Empty until then.
  vbankId: string
}

const VBANK_POLL_INTERVAL_MS = 15_000

// Razorpay data container for the shared BankTransferDrawerView. The recharge
// modal resolves the vbank (useEnsureActiveRazorpayVbank) and passes
// its id before opening. Differs from the Cashfree container in that the vbank
// id is supplied (no list-by-account) and credits are detected on payment_id +
// status 'captured' (Razorpay), not cf_payment_id + 'SUCCESS' (Cashfree).
export const RazorpayBankTransferDrawer = ({
  open,
  onClose,
  vbankId,
}: RazorpayBankTransferDrawerProps) => {
  const { data: vbank, isLoading } = useRazorpayVbankQuery(vbankId, open)
  const isActive = vbank?.status === 'active'
  const bankReceiver = vbank?.receivers?.find((r) => r.entity === 'bank_account')
  const accountId = getAccountId()
  const queryClient = useQueryClient()

  const { data: vbankPayments } = useRazorpayVbankPaymentsQuery(vbankId, {
    enabled: open && !!vbankId,
    refetchInterval: open && vbankId ? VBANK_POLL_INTERVAL_MS : undefined,
  })

  // Baseline = terminal (captured / failed) payment_ids from the first poll
  // while the drawer is open, i.e. the history the customer already knows about.
  // In-flight statuses are deliberately excluded (mirrors the Cashfree drawer's
  // PENDING-excluded baseline) so their transition into captured or failed on a
  // later poll is still detected.
  const baselineRef = useRef<Set<string> | null>(null)
  const [hasHandledNewPayment, setHasHandledNewPayment] = useState(false)

  useEffect(() => {
    if (!open) {
      baselineRef.current = null
      setHasHandledNewPayment(false)
    }
  }, [open])

  useEffect(() => {
    if (!open || !vbankPayments) return
    if (baselineRef.current === null) {
      baselineRef.current = baselinePaymentIds(vbankPayments)
      return
    }
    if (hasHandledNewPayment) return

    const newCaptured = findNewCapturedPayment(vbankPayments, baselineRef.current)
    if (newCaptured) {
      if (accountId) {
        queryClient.invalidateQueries({ queryKey: billingQueryKeys().balanceAll(accountId) })
        queryClient.invalidateQueries({ queryKey: billingQueryKeys().transactionsAll(accountId) })
      }
      successToast({
        title: 'Payment received',
        description: 'Your balance will reflect shortly.',
      })
      setHasHandledNewPayment(true)
      onClose()
      return
    }

    const newFailed = findNewFailedPayment(vbankPayments, baselineRef.current)
    if (newFailed) {
      baselineRef.current.add(newFailed.payment_id)
      errorToast({
        title: 'Payment failed',
        description: 'Bank transfer did not succeed. Please try again.',
      })
    }
  }, [open, vbankPayments, hasHandledNewPayment, accountId, queryClient, onClose])

  return (
    <BankTransferDrawerView
      open={open}
      onClose={onClose}
      isLoading={isLoading}
      // Only an active virtual account can receive funds; one closed after
      // provisioning falls through to the view's error state rather than
      // showing details the customer shouldn't transfer to.
      accountNumber={isActive ? bankReceiver?.account_number : undefined}
      ifscCode={bankReceiver?.ifsc}
      bankName={bankReceiver?.bank_name}
    />
  )
}
